import { useMemo } from 'react';
import { useForm } from 'react-hook-form-mui';

import { useGetInvoiceData } from 'src/use-cases/internalDashboard/useFileResolution';
import { useInvoiceSummary, useReprocessShipmentInvoice } from 'src/use-cases/invoice/useInvoices';
import useToast from 'src/use-cases/toast/useToast';
import { beautifyEnum } from 'src/utils/utility';
import { Divider } from '@mui/material';
import { ROW_ACTION_TYPE, TUpdateValues } from './types';
import { ModalUpdateBase } from './ModalUpdateBase';
import { useInternalDashboardSelector } from './Selector';
import { FormReprocessShipment } from './FormReprocessShipment';
import TransportMediumIcon from '../../molecules/card/TransportMediumIcon';
import { TransportMedium } from '../../molecules/card/types';
import StatusChip from '../dashboard/StatusChip';

const actionType = ROW_ACTION_TYPE.REPROCESS_SHIPMENT;
const defaultValues = {
  shipmentInvoiceId: [] as string[],
};

export const ModalReprocessShipment = () => {
  const { mutateAsync: reprocessShipment } = useReprocessShipmentInvoice();
  const { selectedRow, setIsActionLoading, stopRowAction, checkRowAction, rowActionType } = useInternalDashboardSelector();
  const openToast = useToast();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isOpen = useMemo(() => checkRowAction(actionType), [rowActionType]);
  const { data: dataInvoiceSummary, isLoading } = useInvoiceSummary(selectedRow?.invoiceId, { enabled: isOpen });
  const { data: dataOcrMessage } = useGetInvoiceData(selectedRow?.invoiceFileProcessingId, isOpen);

  const formContext = useForm({
    defaultValues: defaultValues as TUpdateValues,
    mode: 'onChange',
  });

  const onSubmitForm = async (data: TUpdateValues) => {
    // Get the latest value directly from the form
    const selectedShipmentIds = data.shipmentInvoiceId as string[];

    if (!selectedShipmentIds || selectedShipmentIds.length === 0) {
      openToast({ toastType: 'error', title: 'Please select at least one shipment to reprocess' });
      return;
    }

    if (!dataOcrMessage?.message) {
      openToast({ toastType: 'error', title: 'No message data available for reprocessing' });
      return;
    }

    if (!dataInvoiceSummary?.shipmentList) {
      openToast({ toastType: 'error', title: 'Shipment data not available' });
      return;
    }

    setIsActionLoading(true);

    try {
      // Find all selected shipments from all shipment lists
      const allShipments = [
        ...(dataInvoiceSummary.shipmentList.ACTION_NEEDED || []),
        ...(dataInvoiceSummary.shipmentList.PENDING || []),
        ...(dataInvoiceSummary.shipmentList.RESOLVED || []),
        ...(dataInvoiceSummary.shipmentList.PROCESSING || []),
        ...(dataInvoiceSummary.shipmentList.UNDER_REVIEW || []),
      ];

      // Collect all shipment data for batch processing
      const shipmentMessages: Record<string, any> = {};
      const validShipmentIds: string[] = [];
      const invalidShipments: { shipmentId: string; error: string }[] = [];

      // Prepare data for batch processing
      for (const shipmentId of selectedShipmentIds) {
        try {
          // Find the selected shipment
          const selectedShipment = allShipments.find((shipment) => shipment.shipmentInvoiceId === shipmentId);

          if (!selectedShipment || !selectedShipment.shipmentNumber) {
            invalidShipments.push({
              shipmentId,
              error: `Could not find shipment number for shipment ID: ${shipmentId}`,
            });
          } else {
            // Find the OCR message for the shipment using the shipmentNumber
            const shipmentOcrMsg = dataOcrMessage?.message?.shipments?.find((shipment) => shipment.shipmentNumber === selectedShipment.shipmentNumber);

            if (!shipmentOcrMsg) {
              invalidShipments.push({
                shipmentId,
                error: `No OCR data found for shipment: ${selectedShipment.shipmentNumber}`,
              });
            } else {
              // Add to valid shipments for batch processing
              validShipmentIds.push(shipmentId);
              shipmentMessages[shipmentId] = shipmentOcrMsg;
            }
          }
        } catch (error) {
          invalidShipments.push({
            shipmentId,
            error: error instanceof Error ? error.message : 'Unknown error occurred',
          });
        }
      }

      // Make a single API call for all valid shipments
      let apiSuccess = false;
      if (validShipmentIds.length > 0) {
        try {
          await reprocessShipment({
            shipmentInvoiceId: validShipmentIds,
            message: Object.values(shipmentMessages),
          });
          apiSuccess = true;
        } catch (error) {
          console.error('Error in batch reprocessing shipments:', error);
          // All shipments failed due to API error
          validShipmentIds.forEach((id) => {
            invalidShipments.push({
              shipmentId: id,
              error: error instanceof Error ? error.message : 'API call failed',
            });
          });
          validShipmentIds.length = 0; // Clear valid shipments as they all failed
        }
      }

      // Prepare results for UI feedback
      const successCount = validShipmentIds.length;
      const failureCount = invalidShipments.length;

      if (successCount > 0 && failureCount === 0) {
        openToast({
          toastType: 'success',
          title: `${successCount} shipment${successCount > 1 ? 's' : ''} reprocessing initiated successfully`,
        });
        stopRowAction();
      } else if (successCount > 0 && failureCount > 0) {
        openToast({
          toastType: 'warning',
          title: `${successCount} shipment${successCount > 1 ? 's' : ''} reprocessed, ${failureCount} failed`,
          description: 'Some shipments could not be reprocessed. Please check the logs for details.',
        });
        stopRowAction();
      } else {
        openToast({
          toastType: 'error',
          title: 'Failed to reprocess all shipments',
          description: 'None of the selected shipments could be reprocessed. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error reprocessing shipments:', error);
      openToast({
        toastType: 'error',
        title: 'Failed to reprocess shipments',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <ModalUpdateBase title='Reprocess Shipment' actionType={ROW_ACTION_TYPE.REPROCESS_SHIPMENT} formContext={formContext} onSubmit={onSubmitForm}>
      <>
        <div className='bg-neutral-10 rounded-10 p-16'>
          {dataInvoiceSummary && (
            <>
              <div className='flex items-center gap-10'>
                <div className='flex items-center justify-center'>
                  <TransportMediumIcon medium={dataInvoiceSummary?.modality as TransportMedium} iconColor='#9F9F9F' size='md' />
                </div>
                <h2 className='text-16 font-bold text-blue_grey-80'>{beautifyEnum(dataInvoiceSummary?.serviceProvider)?.toUpperCase()}</h2>
                <div className='w-4 h-4 rounded-full bg-[#D9D9D9]' />
                <div className='uppercase text-neutral-60 text-14'>{dataInvoiceSummary?.modality}</div>
              </div>

              <div className='flex items-center gap-16 mt-6'>
                <p className='flex items-center gap-4 text-14'>
                  Invoice No.
                  <strong className='text-16 font-bold text-primary'>{dataInvoiceSummary?.invoiceNumber}</strong>
                </p>
                <Divider className='bg-neutral-20' orientation='vertical' variant='middle' flexItem />
                <StatusChip status={dataInvoiceSummary?.status} size='small' />
              </div>
            </>
          )}
        </div>
        <p className='text-16 text-blue_grey-80 mt-24'>
          <p className='text-16 text-blue_grey-80 mt-24'>Choose the shipments that need to be reprocessed:</p>
        </p>
        <div className='mt-20'>
          <FormReprocessShipment invoiceSummary={dataInvoiceSummary} isLoading={isLoading} />
        </div>
      </>
    </ModalUpdateBase>
  );
};
